/**
 * ModuleErrorBoundary — iter361.
 *
 * Replaces the silent white-screen with a structured error UI showing:
 *   - friendly title + module name
 *   - stack trace + error message (collapsible)
 *   - reload + dashboard buttons
 *
 * Wraps every admin module via <Outlet /> in AdminLayout so a crash in a
 * single page never breaks the whole app.
 */
import React from "react";
import { AlertTriangle, RefreshCw, Home, ChevronDown } from "lucide-react";

export default class ModuleErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ModuleErrorBoundary caught:", error, info?.componentStack?.slice(0, 800));
    this.setState({ info });
    // Best-effort: send to backend for centralized logging.
    try {
      const url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/v1/system/client-error";
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: error?.message || String(error),
          stack: (error?.stack || "").slice(0, 4000),
          component_stack: (info?.componentStack || "").slice(0, 4000),
          path: window.location.pathname + window.location.search,
          user_agent: navigator.userAgent.slice(0, 240),
          ts: new Date().toISOString(),
        }),
      }).catch(() => {});
    } catch { /* swallow */ }
  }

  reset = () => this.setState({ hasError: false, error: null, info: null, showDetails: false });

  componentDidUpdate(prev) {
    // Auto-reset if route changes (so going back to a working page recovers).
    if (prev.routeKey !== this.props.routeKey && this.state.hasError) {
      this.reset();
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const err = this.state.error;
    const msg = err?.message || String(err) || "Neznámá chyba";
    const stack = (err?.stack || "").slice(0, 2000);
    const compStack = (this.state.info?.componentStack || "").slice(0, 1500);
    return (
      <div className="max-w-3xl mx-auto py-12 px-4" data-testid="module-error-boundary">
        <div className="bg-white border-2 border-red-200 rounded-2xl shadow-lg overflow-hidden">
          <div className="px-6 py-5 bg-red-50 border-b border-red-200 flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-red-900">Modul nelze načíst</h2>
              <p className="text-sm text-red-700 mt-1">
                Nastala chyba při zobrazení této stránky. Aplikace zůstala funkční — můžete pokračovat
                v jiných modulech přes menu vlevo nebo stránku obnovit.
              </p>
              <p className="text-[11px] text-red-500 font-mono mt-2 break-all">{msg}</p>
            </div>
          </div>

          <div className="px-6 py-4 flex gap-2 flex-wrap">
            <button
              onClick={this.reset}
              data-testid="module-error-retry"
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-semibold inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Zkusit znovu
            </button>
            <button
              onClick={() => window.location.reload()}
              data-testid="module-error-reload"
              className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-semibold inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Obnovit stránku
            </button>
            <button
              onClick={() => { window.location.href = "/admin/dashboard"; }}
              data-testid="module-error-home"
              className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-semibold inline-flex items-center gap-2"
            >
              <Home className="w-4 h-4" /> Přehled
            </button>
            <button
              onClick={() => this.setState((s) => ({ showDetails: !s.showDetails }))}
              data-testid="module-error-toggle-details"
              className="px-3 py-2 text-slate-500 hover:text-slate-700 text-xs inline-flex items-center gap-1.5"
            >
              <ChevronDown className={`w-3 h-3 transition-transform ${this.state.showDetails ? "rotate-180" : ""}`} />
              {this.state.showDetails ? "Skrýt detaily" : "Zobrazit detaily chyby"}
            </button>
          </div>

          {this.state.showDetails && (
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Stack trace</h4>
              <pre className="text-[10px] text-slate-600 font-mono whitespace-pre-wrap bg-white border border-slate-200 rounded-lg p-3 max-h-64 overflow-auto">
{stack || "(no stack)"}
              </pre>
              {compStack && (
                <>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-3 mb-2">Component stack</h4>
                  <pre className="text-[10px] text-slate-600 font-mono whitespace-pre-wrap bg-white border border-slate-200 rounded-lg p-3 max-h-48 overflow-auto">
{compStack}
                  </pre>
                </>
              )}
              <p className="text-[10px] text-slate-400 mt-2">
                Chyba byla automaticky odeslána na server pro analýzu (Admin → Aktivita → Client Errors).
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
}
